import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { GradingToolUsageAccuracy } from "@repo/app-types/entities/toolUsageAccuracy";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useAllToolsById } from "../../hooks/useAllToolsById.js";
import { ToolUsageAccuracyCallSlideoverDialogContainer } from "../agents/toolUsageAccuracyCallSlideoverDialogContainer.js";
import { ToolUsageAccuracyCard } from "./toolUsageAccuracyCard.js";

export interface ToolUsageAccuracyCardContainerProps {
  data: GradingToolUsageAccuracy | null | undefined;
  /** Required for opening the call slideover. */
  gradingId?: GradingId;
}

interface OpenedTool {
  toolId: string;
  toolName: string;
}

/**
 * Container component that fetches tools and renders ToolUsageAccuracyCard.
 */
export const ToolUsageAccuracyCardContainer: FC<
  ToolUsageAccuracyCardContainerProps
> = (props) => {
  const { data: toolUsageAccuracy, gradingId } = props;

  const allTools = useAllToolsById();

  const [openedTool, setOpenedTool] = useState<OpenedTool | undefined>(
    undefined,
  );

  const canOpenSlideover = gradingId !== undefined;

  const handleToolClick = useCallback(
    (toolId: string, toolName: string): void => {
      if (!canOpenSlideover) {
        return;
      }
      setOpenedTool({ toolId, toolName });
    },
    [canOpenSlideover],
  );

  const handleSlideoverOpenChange = (newIsOpen: boolean): void => {
    if (!newIsOpen) {
      setOpenedTool(undefined);
    }
  };

  return (
    <>
      <ToolUsageAccuracyCard
        data={toolUsageAccuracy}
        tools={allTools}
        onToolClick={canOpenSlideover ? handleToolClick : undefined}
      />

      <SlideoverContainer
        isDismissable
        isOpen={openedTool !== undefined}
        onOpenChange={handleSlideoverOpenChange}
      >
        {openedTool && gradingId ? (
          <ToolUsageAccuracyCallSlideoverDialogContainer
            gradingId={gradingId}
            toolId={openedTool.toolId}
            toolName={openedTool.toolName}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};
